"use client";

import * as React from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const chartData = [{
  month: "Jan",
  movement: 1240
}, {
  month: "Feb",
  movement: 1380
}, {
  month: "Mar",
  movement: 1320
}, {
  month: "Apr",
  movement: 1550
}, {
  month: "May",
  movement: 1480
}, {
  month: "Jun",
  movement: 1720
}, {
  month: "Jul",
  movement: 1650
}, {
  month: "Aug",
  movement: 1890
}, {
  month: "Sep",
  movement: 2100
}, {
  month: "Oct",
  movement: 1950
}, {
  month: "Nov",
  movement: 2340
}, {
  month: "Dec",
  movement: 2580
}];
const chartConfig = {
  movement: {
    label: "Movement Index",
    color: "var(--primary)"
  }
} satisfies ChartConfig;
const commentaryData = [{
  id: "col-1",
  title: "Market Dynamics & Volatility",
  content: "This month observed a significant upward trajectory in core metrics, driven primarily by increased engagement in the secondary market sectors. The volatility index remained relatively stable, suggesting a maturation of the current trend cycle rather than a temporary spike. We anticipate this baseline to hold through the next quarter, providing a solid foundation for sustained growth."
}, {
  id: "col-2",
  title: "Forward Outlook & Strategy",
  content: "Analyzing the moving averages indicates a strong support level forming around the mid-tier data points. While external macroeconomic factors continue to introduce minor fluctuations, the underlying momentum remains robust. Strategic positioning should focus on capitalizing on these established support levels while maintaining liquidity for opportunistic entries in the coming months."
}];
export default function MonthlyMovement() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="monthly-movement" className="bg-background py-16 md:py-24 overflow-hidden" ref={ref}>
      <div className={`container mx-auto px-4 md:px-6 max-w-6xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="mb-10">
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground tracking-tight mb-4">
            Monthly Movement
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl">
            An interactive overview of our primary performance metrics over the trailing twelve-month period.
          </p>
        </div>

        {}
        <div className="border border-border rounded-xl p-4 md:p-8 bg-card shadow-sm mb-12">
          <ChartContainer config={chartConfig} className="min-h-[350px] w-full">
            <LineChart accessibilityLayer data={chartData} margin={{
            left: -20,
            right: 12,
            top: 24,
            bottom: 12
          }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" opacity={0.5} />
              <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={12} tickFormatter={value => value.slice(0, 3)} className="text-muted-foreground font-sans text-xs" />
              <YAxis tickLine={false} axisLine={false} tickMargin={12} className="text-muted-foreground font-sans text-xs" tickFormatter={value => `${value}`} />
              <ChartTooltip cursor={{
              stroke: "var(--border)",
              strokeWidth: 1,
              strokeDasharray: "4 4"
            }} content={<ChartTooltipContent className="font-mono text-sm shadow-lg border-border/50" indicator="dot" />} />
              <Line dataKey="movement" type="monotone" stroke="var(--color-movement)" strokeWidth={3} dot={{
              fill: "var(--background)",
              stroke: "var(--color-movement)",
              strokeWidth: 2,
              r: 4
            }} activeDot={{
              fill: "var(--color-movement)",
              stroke: "var(--background)",
              strokeWidth: 2,
              r: 6
            }} animationDuration={1500} animationEasing="ease-out" />
            </LineChart>
          </ChartContainer>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {commentaryData.map((column, i) => <div key={column.id} data-index={i} className="flex flex-col gap-3">
              <h3 className="text-lg font-semibold font-default text-foreground">
                {column.title}
              </h3>
              <p className="text-base leading-[1.6] font-sans text-muted-foreground">
                {column.content}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}